'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import './styles.css';

import { ArrowRightIcon } from '@heroicons/react/20/solid';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { twJoin } from 'tailwind-merge';
import { buildTrackingId } from '@/utils/tracking-helper';
import OptimizedImage from '../../shared/OptimizedImage';
import Container from '../../shared/Container';
import { RichTextRenderer } from '../../tileRenderers/RichTextRenderer';
import { ThreeColumnSliderTile } from './types/ThreeColumnSliderTile';

export default function ThreeColumnSlider({
  tile,
  trackingPrefix,
}: Readonly<{
  tile: ThreeColumnSliderTile;
  trackingPrefix?: string;
}>) {
  const [sliderHeight, setSliderHeight] = useState(0);
  const slideRef = useRef<HTMLDivElement | null>(null);
  const height = tile.height?.options?.[0]?.value;
  const { localizedLink } = useLocaleLink();

  useEffect(() => {
    if (slideRef.current?.clientWidth) {
      const width = slideRef.current.clientWidth;
      // adding 32px because of the bottom padding
      switch (height) {
        case 'xs':
          // 4:3
          setSliderHeight(width * (3 / 4) + 32);
          break;
        case 'sm':
          // 16:9
          setSliderHeight(width * (9 / 16) + 32);
          break;
        case 'md':
          // 1:1
          setSliderHeight(width + 32);
          break;
        case 'lg':
          // 3:4
          setSliderHeight(width * (4 / 3) + 32);
          break;
        case 'xl':
          // 9:16
          setSliderHeight(width * (16 / 9) + 32);
          break;
        default:
          // 3:4
          setSliderHeight(width * (4 / 3) + 32);
          break;
      }
    }
  }, []);

  return (
    <Container>
      <Swiper
        spaceBetween={16}
        slidesPerView={1.4}
        breakpoints={{
          640: {
            slidesPerView: 2.4,
            spaceBetween: 20,
          },
          1240: {
            slidesPerView: 3,
            spaceBetween: 32,
          },
        }}
        modules={[Pagination, Navigation]}
        navigation
        pagination={{
          clickable: true,
        }}
        className="three-column-slider w-full"
        style={{ height: sliderHeight }}
      >
        {tile.cards.chunks.map((card, i) => {
          const title = extractComponentFromChunk(card, 'title', 'RichTextContent');
          const textColor = extractComponentFromChunk(card, 'text-color', 'SelectionContent')
            ?.options?.[0]?.value;
          const removeGradient = extractComponentFromChunk(
            card,
            'remove-gradient',
            'BooleanContent',
          )?.value;
          const image = extractComponentFromChunk(card, 'image', 'ImageContent');
          const link = extractComponentFromChunk(card, 'internal-link', 'ItemRelationsContent')
            ?.items?.[0]?.path;

          let textColorClass;
          switch (textColor) {
            case 'black':
              textColorClass = 'text-black';
              break;
            case 'white':
              textColorClass = 'text-white';
              break;
            default:
              textColorClass = 'text-white';
              break;
          }

          const titleText = title?.plainText?.[0];
          const slideTrackingId = buildTrackingId(trackingPrefix, titleText || `slide-${i}`);

          return (
            <SwiperSlide
              key={`${i}three-col-slider`}
              className="group relative z-0 overflow-hidden rounded-xl"
              // Ignore TS because the ref gets passed to the SwiperSlide
              // element but it's not in the type
              // @ts-ignore
              ref={slideRef}
            >
              <OptimizedImage
                imageComponent={image}
                fill
                className="-z-20 object-cover"
                sizes="(max-width: 640px) 60vw, (max-width: 1240px) 40vw, 30vw"
              />
              {!removeGradient && (
                <div className="absolute inset-0 top-1/2 z-10 bg-gradient-to-t from-neutral-100/40 to-transparent" />
              )}
              <div className="absolute bottom-0 z-20 flex w-full items-center justify-between gap-6 px-4 pb-4 lg:px-6 lg:pb-6">
                <RichTextRenderer
                  blocks={title}
                  className={twJoin('text-2xl font-bold lg:text-3xl', textColorClass)}
                />
                <ArrowRightIcon className="h-auto w-6 flex-shrink-0 self-end text-primary-100 duration-200 ease-in group-hover:translate-x-2 lg:w-9" />
              </div>
              {link && (
                <Link
                  href={localizedLink(link)}
                  className="absolute inset-0 z-20"
                  aria-label={titleText || ''}
                  data-tile-element-id={slideTrackingId}
                />
              )}
            </SwiperSlide>
          );
        })}
      </Swiper>
    </Container>
  );
}
